import { type ReactElement } from 'react';
import { ArrowRight, Check } from 'lucide-react';

/**
 * Hero der Startseite.
 *
 * Bewusst ruhig gehalten: keine Farbverlaufs-Flecken, keine schwebenden
 * Punkte, keine Einblend-Animationen. Buchhaltungssoftware verwaltet Geld und
 * Steuerdaten — Verspieltheit kostet hier Vertrauen. Blau ist die einzige
 * Akzentfarbe, alles andere sind Grautoene.
 *
 * Die Ueberschrift nennt die Zielgruppe statt der Kategorie: "Buchhaltung.
 * Einfach. Online." sagt niemandem, ob er gemeint ist.
 */

/** Was die Software konkret tut — keine Siegel, keine Behauptungen. */
const leistungen = [
  'Rechnungen und Angebote schreiben',
  'Belege abfotografieren statt abtippen',
  'EÜR und Umsatzsteuer direkt an ELSTER',
];

export default function Hero(): ReactElement {
  return (
    <section className="relative overflow-hidden border-b border-secondary-100 bg-white pt-24 lg:pt-32">
      {/* Ein einziger, sehr dezenter Farbton nach oben — kein Blickfang,
          nur eine weiche Kante zum Header. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-primary-50/60 to-transparent" />

      <div className="relative mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8 lg:pb-24">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Textspalte */}
          <div>
            <h1 className="text-4xl font-bold leading-[1.15] tracking-tight text-secondary-900 sm:text-5xl">
              Buchhaltung für Selbstständige und
              <br className="hidden lg:block" /> kleine Unternehmen
            </h1>

            <p className="mt-6 max-w-xl text-lg leading-relaxed text-secondary-600">
              Saldify bündelt Rechnungen, Belege und Steuern in einer Anwendung – von der
              ersten Rechnung bis zur Übermittlung ans Finanzamt. Ohne Vorkenntnisse
              bedienbar, auch wenn du noch nie gebucht hast.
            </p>

            <ul className="mt-8 space-y-3">
              {leistungen.map((text) => (
                <li key={text} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" aria-hidden="true" />
                  <span className="text-base text-secondary-700">{text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href="/register"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-6 py-3.5 text-base font-semibold text-white transition hover:bg-primary-700"
              >
                Kostenlos starten
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="/preise"
                className="inline-flex items-center justify-center rounded-lg border border-secondary-300 px-6 py-3.5 text-base font-semibold text-secondary-700 transition hover:border-secondary-400 hover:bg-secondary-50"
              >
                Preise ansehen
              </a>
            </div>

            {/* Konkret statt Siegel: Das sind nachpruefbare Aussagen. */}
            <p className="mt-6 text-sm text-secondary-500">
              Kostenlos testen, keine Zahlungsdaten nötig. Server in Deutschland,
              DSGVO-konform.
            </p>
          </div>

          {/* Produktbild. Ohne Browser-Attrappe und ohne Schraeglage — das
              Bild zeigt die echte Anwendung, das reicht. */}
          <div className="relative">
            <div className="overflow-hidden rounded-xl border border-secondary-200 bg-white shadow-lg shadow-secondary-900/5">
              <img
                src="/screenshots/hero-rechnung-erstellen.webp"
                alt="Rechnung in Saldify erstellen: Positionen, Rabatt und automatisch berechnete Umsatzsteuer"
                width={1316}
                height={1198}
                loading="eager"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
